import api from './api.interceptor';
import { API_BASE_URL } from '../config/api.config';

export interface ProfileVisibility {
  value: 'public' | 'private' | 'recruiters-only';
  label: string;
}

export interface UserPreferences {
  emailNotifications: boolean;
  jobAlerts: boolean;
  marketingEmails: boolean;
  profileVisibility: 'public' | 'private' | 'recruiters-only';
  darkMode: boolean;
}

export interface ReferralNotifications {
  newMatches: boolean;
  referralUpdates: boolean;
  paymentAlerts: boolean;
}

export interface ReferralPreferences {
  minBounty: number;
  maxBounty: number;
  industries: string[];
  companySizes: string[];
  notifications: ReferralNotifications;
}

export interface ResumeExperience {
  id: string;
  title: string;
  company: string;
  location: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  description: string;
}

export interface ResumeEducation {
  id: string;
  degree: string;
  field: string;
  school: string;
  location: string;
  startDate: string;
  endDate?: string;
  gpa?: string;
}

export interface ResumeCertification {
  id: string;
  name: string;
  issuer: string;
  date: string;
  expiryDate?: string;
  credentialId?: string;
  url?: string;
}

export interface ResumeData {
  summary?: string;
  experience: ResumeExperience[];
  education: ResumeEducation[];
  skills: string[];
  certifications: ResumeCertification[];
  languages?: string[];
  projects?: Array<{
    id: string;
    name: string;
    description: string;
    technologies: string[];
    url?: string;
  }>;
}

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  photoUrl?: string;
  userType: 'candidate' | 'employer';
  
  // Profile details
  headline?: string;
  location?: {
    city?: string;
    country?: string;
    remote?: boolean;
  };
  
  // Professional details
  currentRole?: string;
  company?: string;
  yearsExperience?: '0-2' | '3-5' | '6-10' | '10+';
  skills?: string[];
  industries?: string[];
  
  // Job preferences
  jobPreferences?: {
    jobTypes?: string[];
    locations?: string[];
    remoteOnly?: boolean;
    salaryRange?: {
      min: number;
      max: number;
    };
  };
  
  // Settings
  preferences?: UserPreferences;
  referralPreferences?: ReferralPreferences;
  
  // Resume
  resume?: {
    url: string;
    uploadedAt: string;
  };
  resumeData?: ResumeData;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
}

export interface UpdateProfileData {
  fullName?: string;
  headline?: string;
  location?: {
    city?: string;
    country?: string;
    remote?: boolean;
  };
  currentRole?: string;
  company?: string;
  yearsExperience?: '0-2' | '3-5' | '6-10' | '10+';
  skills?: string[];
  industries?: string[];
  jobPreferences?: {
    jobTypes?: string[];
    locations?: string[];
    remoteOnly?: boolean;
    salaryRange?: {
      min: number;
      max: number;
    };
  };
  preferences?: UserPreferences;
  referralPreferences?: ReferralPreferences;
}

class ProfileService {
  private baseUrl = `${API_BASE_URL}/profile`;

  /**
   * Get current user's profile
   */
  async getProfile(): Promise<UserProfile> {
    try {
      const response = await api.get(this.baseUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }
      
      const data = await response.json();
      return this.mapBackendToFrontend(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(updates: UpdateProfileData): Promise<UserProfile> {
    try {
      const backendData = this.mapFrontendToBackend(updates);
      const response = await api.put(this.baseUrl, backendData);
      
      if (!response.ok) {
        throw new Error('Failed to update profile');
      }
      
      const data = await response.json();
      return this.mapBackendToFrontend(data);
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Upload resume file
   */
  async uploadResume(file: File): Promise<{ url: string; uploadedAt: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`${this.baseUrl}/resume/upload`, formData);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload resume');
      }
      
      const data = await response.json();
      return {
        url: data.url,
        uploadedAt: data.uploaded_at
      };
    } catch (error) {
      console.error('Error uploading resume:', error);
      throw error;
    }
  }

  /**
   * Delete resume
   */
  async deleteResume(): Promise<void> {
    try {
      const response = await api.delete(`${this.baseUrl}/resume`);
      
      if (!response.ok) {
        throw new Error('Failed to delete resume');
      }
    } catch (error) {
      console.error('Error deleting resume:', error);
      throw error;
    }
  }

  /**
   * Get resume download URL
   */
  async getResumeDownloadUrl(): Promise<string> {
    try {
      const response = await api.get(`${this.baseUrl}/resume/download`);
      
      if (!response.ok) {
        throw new Error('Failed to get resume download URL');
      }
      
      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error getting resume download URL:', error);
      throw error;
    }
  }

  /**
   * Get resume builder data
   */
  async getResumeData(): Promise<ResumeData | null> {
    try {
      const response = await api.get(`${this.baseUrl}/resume-data`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('Failed to fetch resume data');
      }
      
      const data = await response.json();
      return this.mapResumeDataFromBackend(data);
    } catch (error) {
      console.error('Error fetching resume data:', error);
      throw error;
    }
  }

  /**
   * Update resume builder data
   */
  async updateResumeData(resumeData: ResumeData): Promise<ResumeData> {
    try {
      const backendData = this.mapResumeDataToBackend(resumeData);
      const response = await api.put(`${this.baseUrl}/resume-data`, backendData);
      
      if (!response.ok) {
        throw new Error('Failed to update resume data');
      }
      
      const data = await response.json();
      return this.mapResumeDataFromBackend(data);
    } catch (error) {
      console.error('Error updating resume data:', error);
      throw error;
    }
  }

  /**
   * Map backend user data to frontend format
   */
  private mapBackendToFrontend(backendData: any): UserProfile {
    return {
      id: backendData.id,
      email: backendData.email,
      fullName: backendData.full_name || backendData.fullName || '',
      photoUrl: backendData.photo_url || backendData.photoUrl,
      userType: backendData.user_type || backendData.userType || 'candidate',
      
      headline: backendData.headline,
      location: backendData.location ? {
        city: backendData.location.city,
        country: backendData.location.country,
        remote: backendData.location.remote
      } : undefined,
      
      currentRole: backendData.current_role || backendData.currentRole,
      company: backendData.company,
      yearsExperience: backendData.years_experience || backendData.yearsExperience,
      skills: backendData.skills || [],
      industries: backendData.industries || [],
      
      jobPreferences: backendData.job_preferences ? {
        jobTypes: backendData.job_preferences.job_types || backendData.job_preferences.jobTypes,
        locations: backendData.job_preferences.locations,
        remoteOnly: backendData.job_preferences.remote_only || backendData.job_preferences.remoteOnly,
        salaryRange: backendData.job_preferences.salary_range ? {
          min: backendData.job_preferences.salary_range.min,
          max: backendData.job_preferences.salary_range.max
        } : undefined
      } : undefined,
      
      preferences: backendData.preferences ? {
        emailNotifications: backendData.preferences.email_notifications,
        jobAlerts: backendData.preferences.job_alerts,
        marketingEmails: backendData.preferences.marketing_emails,
        profileVisibility: backendData.preferences.profile_visibility,
        darkMode: backendData.preferences.dark_mode
      } : undefined,
      
      referralPreferences: backendData.referral_preferences ? {
        minBounty: backendData.referral_preferences.min_bounty,
        maxBounty: backendData.referral_preferences.max_bounty,
        industries: backendData.referral_preferences.industries || [],
        companySizes: backendData.referral_preferences.company_sizes || [],
        notifications: {
          newMatches: backendData.referral_preferences.notifications?.new_matches ?? true,
          referralUpdates: backendData.referral_preferences.notifications?.referral_updates ?? true,
          paymentAlerts: backendData.referral_preferences.notifications?.payment_alerts ?? true
        }
      } : undefined,
      
      resume: backendData.resume ? {
        url: backendData.resume.url,
        uploadedAt: backendData.resume.uploaded_at
      } : undefined,
      
      resumeData: backendData.resume_data ? this.mapResumeDataFromBackend(backendData.resume_data) : undefined,
      
      createdAt: backendData.created_at,
      updatedAt: backendData.updated_at,
      lastLogin: backendData.last_login
    };
  }

  /**
   * Map frontend update data to backend format
   */
  private mapFrontendToBackend(frontendData: UpdateProfileData): any {
    const backendData: any = {};
    
    if (frontendData.fullName !== undefined) {
      backendData.full_name = frontendData.fullName;
    }
    
    if (frontendData.headline !== undefined) {
      backendData.headline = frontendData.headline;
    }
    
    if (frontendData.location !== undefined) {
      backendData.location = frontendData.location;
    }
    
    if (frontendData.currentRole !== undefined) {
      backendData.current_role = frontendData.currentRole;
    }
    
    if (frontendData.company !== undefined) {
      backendData.company = frontendData.company;
    }
    
    if (frontendData.yearsExperience !== undefined) {
      backendData.years_experience = frontendData.yearsExperience;
    }
    
    if (frontendData.skills !== undefined) {
      backendData.skills = frontendData.skills;
    }
    
    if (frontendData.industries !== undefined) {
      backendData.industries = frontendData.industries;
    }
    
    if (frontendData.jobPreferences !== undefined) {
      backendData.job_preferences = {
        job_types: frontendData.jobPreferences.jobTypes,
        locations: frontendData.jobPreferences.locations,
        remote_only: frontendData.jobPreferences.remoteOnly,
        salary_range: frontendData.jobPreferences.salaryRange
      };
    }
    
    if (frontendData.preferences !== undefined) {
      backendData.preferences = {
        email_notifications: frontendData.preferences.emailNotifications,
        job_alerts: frontendData.preferences.jobAlerts,
        marketing_emails: frontendData.preferences.marketingEmails,
        profile_visibility: frontendData.preferences.profileVisibility,
        dark_mode: frontendData.preferences.darkMode
      };
    }
    
    if (frontendData.referralPreferences !== undefined) {
      backendData.referral_preferences = {
        min_bounty: frontendData.referralPreferences.minBounty,
        max_bounty: frontendData.referralPreferences.maxBounty,
        industries: frontendData.referralPreferences.industries,
        company_sizes: frontendData.referralPreferences.companySizes,
        notifications: {
          new_matches: frontendData.referralPreferences.notifications.newMatches,
          referral_updates: frontendData.referralPreferences.notifications.referralUpdates,
          payment_alerts: frontendData.referralPreferences.notifications.paymentAlerts
        }
      };
    }
    
    return backendData;
  }

  /**
   * Map backend resume data to frontend format
   */
  private mapResumeDataFromBackend(backendData: any): ResumeData {
    return {
      summary: backendData.summary,
      experience: (backendData.experience || []).map((exp: any) => ({
        id: exp.id,
        title: exp.title,
        company: exp.company,
        location: exp.location,
        startDate: exp.start_date,
        endDate: exp.end_date,
        current: exp.current,
        description: exp.description
      })),
      education: (backendData.education || []).map((edu: any) => ({
        id: edu.id,
        degree: edu.degree,
        field: edu.field,
        school: edu.school,
        location: edu.location,
        startDate: edu.start_date,
        endDate: edu.end_date,
        gpa: edu.gpa
      })),
      skills: backendData.skills || [],
      certifications: (backendData.certifications || []).map((cert: any) => ({
        id: cert.id,
        name: cert.name,
        issuer: cert.issuer,
        date: cert.date,
        expiryDate: cert.expiry_date,
        credentialId: cert.credential_id,
        url: cert.url
      })),
      languages: backendData.languages,
      projects: backendData.projects
    };
  }

  /**
   * Map frontend resume data to backend format
   */
  private mapResumeDataToBackend(frontendData: ResumeData): any {
    return {
      summary: frontendData.summary,
      experience: frontendData.experience.map(exp => ({
        id: exp.id,
        title: exp.title,
        company: exp.company,
        location: exp.location,
        start_date: exp.startDate,
        end_date: exp.endDate,
        current: exp.current,
        description: exp.description
      })),
      education: frontendData.education.map(edu => ({
        id: edu.id,
        degree: edu.degree,
        field: edu.field,
        school: edu.school,
        location: edu.location,
        start_date: edu.startDate,
        end_date: edu.endDate,
        gpa: edu.gpa
      })),
      skills: frontendData.skills,
      certifications: frontendData.certifications.map(cert => ({
        id: cert.id,
        name: cert.name,
        issuer: cert.issuer,
        date: cert.date,
        expiry_date: cert.expiryDate,
        credential_id: cert.credentialId,
        url: cert.url
      })),
      languages: frontendData.languages,
      projects: frontendData.projects
    };
  }
}

export const profileService = new ProfileService();
export default profileService;